import {Shift} from '@domain/shifthandover'
import {ApiClient} from '@hconnect/apiclient'
import {FilterTimeRange} from '@hconnect/common/types'
import {generateTimeRange} from '@hconnect/common/utils'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {omit} from 'lodash'

import {mapWorkOrder, mapWorkOrderPagedContainerDto} from '../mappers'
import {
  WorkOrdersFilterOptions,
  WorkOrdersFilterOptionsPayload,
  WorkOrdersPayload,
  WorkOrder,
  WorkOrderDto,
  WorkOrderPagedContainer,
  WorkOrderPagedContainerDto
} from '../types'
import {preparePayload} from '../utils'

const WorkOrdersQueryKey = 'work-orders'
const WorkOrderQueryKey = 'work-order'

const mapPayload = (input: WorkOrdersFilterOptions): WorkOrdersFilterOptionsPayload => {
  const data = {
    ...omit(input, [
      'timeRange',
      'workOrderStatus',
      'sortWorkOrderBy',
      'woSortDir',
      'orderType',
      'equipment',
      'functionalEquipment'
    ]),
    statuses: input.workOrderStatus,
    orderTypes: input.orderType,
    ...(input.equipment && {equipment: input.equipment?.id}),
    ...(input.functionalEquipment && {functionalEquipment: input.functionalEquipment?.id})
  }
  return preparePayload<WorkOrdersFilterOptionsPayload>(data)
}

export const useWorkOrders = (
  plantId: string,
  shifts: Shift[],
  {input, offset, limit}: WorkOrdersPayload<WorkOrdersFilterOptions>,
  options?: Omit<
    UseQueryOptions<WorkOrderPagedContainerDto, AxiosError, WorkOrderPagedContainer>,
    'queryKey'
  >
) => {
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)
  const path = `/work-orders/${plantId}/work-orders`

  return useQuery<WorkOrderPagedContainerDto, AxiosError, WorkOrderPagedContainer>({
    queryKey: [WorkOrdersQueryKey, path, shifts, JSON.stringify(input), offset, limit, timezone],
    queryFn: async () => {
      const timeRange: FilterTimeRange = generateTimeRange(input.timeRange, shifts, getPlantNow)
      const filters = mapPayload(input)
      const response = await ApiClient.axiosInstance.get<WorkOrderPagedContainerDto>(path, {
        params: {
          from: timeRange.startDate,
          to: timeRange.endDate,
          offset,
          limit,
          sortBy: input.sortWorkOrderBy,
          sortingDirection: input.woSortDir,
          ...filters
        }
      })
      return response.data
    },
    ...options,
    select: (data) => mapWorkOrderPagedContainerDto(data, timezone)
  })
}

type UseWorkOrderParams = {
  plantId: string
  workOrderId?: string
  options?: Omit<
    UseQueryOptions<WorkOrderDto | undefined, AxiosError, WorkOrder | undefined>,
    'queryKey'
  >
}

export const useWorkOrder = ({plantId, workOrderId, options}: UseWorkOrderParams) => {
  const timezone = useUpmPlantTimezone(plantId)
  const path = `/work-orders/${plantId}/work-orders/${workOrderId}`

  return useQuery<WorkOrderDto | undefined, AxiosError, WorkOrder | undefined>({
    queryKey: [WorkOrderQueryKey, path],
    queryFn: async () => {
      if (!workOrderId) return undefined
      const {data} = await ApiClient.axiosInstance.get<WorkOrderDto>(path)
      return data
    },
    ...options,
    select: (data) => data && mapWorkOrder(data, timezone)
  })
}
